/* src/components/DraggableFeedback.js - v2.2 - Nexus Icon & Unread Bubble */
import React, { useState, useRef, useEffect, useCallback } from 'react';

function DraggableFeedback({ onClick, unreadCount }) {
    const [position, setPosition] = useState({ x: window.innerWidth - 70, y: window.innerHeight - 150 });
    const [isDragging, setIsDragging] = useState(false);
    
    // Refs for calculating distance
    const dragStartPos = useRef({ x: 0, y: 0 });
    const dragOffset = useRef({ x: 0, y: 0 });
    const hasMoved = useRef(false);

    const handleStart = (clientX, clientY) => {
        setIsDragging(true);
        hasMoved.current = false; // Reset move flag
        
        // Store initial click position to calculate distance later
        dragStartPos.current = { x: clientX, y: clientY };
        
        dragOffset.current = {
            x: clientX - position.x,
            y: clientY - position.y
        };
    };

    const handleMove = useCallback((clientX, clientY) => {
        if (!isDragging) return;

        // Calculate distance moved
        const moveDistance = Math.sqrt(
            Math.pow(clientX - dragStartPos.current.x, 2) + 
            Math.pow(clientY - dragStartPos.current.y, 2)
        );

        // Only consider it a "move" if dragged more than 5 pixels
        if (moveDistance > 5) {
            hasMoved.current = true;
            
            let newX = clientX - dragOffset.current.x;
            let newY = clientY - dragOffset.current.y;

            newX = Math.max(10, Math.min(window.innerWidth - 60, newX));
            newY = Math.max(10, Math.min(window.innerHeight - 60, newY));

            setPosition({ x: newX, y: newY });
        }
    }, [isDragging]);

    const handleEnd = useCallback(() => {
        setIsDragging(false);
        // Only trigger click if we didn't move significantly
        if (!hasMoved.current && onClick) {
            onClick();
        }
    }, [onClick]);

    // Events
    const onMouseDown = (e) => handleStart(e.clientX, e.clientY);
    const onTouchStart = (e) => handleStart(e.touches[0].clientX, e.touches[0].clientY);

    // Global Listeners
    useEffect(() => {
        const onMouseMove = (e) => { if (isDragging) { e.preventDefault(); handleMove(e.clientX, e.clientY); }};
        const onMouseUp = () => { if (isDragging) handleEnd(); };
        const onTouchMove = (e) => { if (isDragging) { e.preventDefault(); handleMove(e.touches[0].clientX, e.touches[0].clientY); }};
        const onTouchEnd = () => { if (isDragging) handleEnd(); };

        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
        };
    }, [isDragging, handleMove, handleEnd]);

    return (
        <div 
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            style={{
                position: 'fixed',
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: '55px',
                height: '55px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0984e3, #74b9ff)',
                boxShadow: '0 5px 20px rgba(9, 132, 227, 0.4)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '30px',
                cursor: isDragging ? 'grabbing' : 'grab',
                zIndex: 99999,
                userSelect: 'none',
                touchAction: 'none',
                transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                transition: isDragging ? 'none' : 'transform 0.2s'
            }}
            title="Chat with Nexus AI"
        >
            {/* --- NEXUS ROBOT ICON --- */}
            🤖
            
            {/* --- NOTIFICATION BUBBLE --- */}
            {unreadCount > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: '#ff4757',
                    color: 'white',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    border: '2px solid #2f3542'
                }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                </div>
            )}
        </div>
    );
}

export default DraggableFeedback;
